import React, { useRef, useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MAX_BYTES = 100 * 1024;

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export async function compressImageTo100KB(file) {
  if (!file || !file.type?.startsWith('image/')) {
    throw new Error('Please select a valid image file');
  }

  const imageUrl = URL.createObjectURL(file);
  const image = new Image();
  image.src = imageUrl;
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });

  let width = image.width;
  let height = image.height;
  let maxSide = 1280;
  let bestBlob = null;

  for (let resizeStep = 0; resizeStep < 8; resizeStep += 1) {
    const ratio = Math.min(1, maxSide / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * ratio));
    const targetHeight = Math.max(1, Math.round(height * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

    for (let quality = 0.82; quality >= 0.35; quality -= 0.07) {
      const blob = await canvasToBlob(canvas, quality);
      if (!blob) continue;
      bestBlob = blob;
      if (blob.size <= MAX_BYTES) {
        URL.revokeObjectURL(imageUrl);
        return {
          dataUrl: await blobToDataUrl(blob),
          size: blob.size,
          name: file.name,
        };
      }
    }

    maxSide = Math.round(maxSide * 0.82);
  }

  URL.revokeObjectURL(imageUrl);
  if (bestBlob && bestBlob.size <= 120 * 1024) {
    return {
      dataUrl: await blobToDataUrl(bestBlob),
      size: bestBlob.size,
      name: file.name,
    };
  }

  throw new Error('Image could not be compressed to 100 KB. Please choose a smaller image.');
}

const TransactionImageField = ({ value, onChange, required = false, disabled = false, label = 'Transaction Photo' }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setError('');
    try {
      const result = await compressImageTo100KB(file);
      onChange?.(result);
    } catch (err) {
      setError(err.message || 'Failed to process image');
      onChange?.(null);
    } finally {
      setIsProcessing(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-600">
          <PhotoIcon className="h-4 w-4 text-indigo-500" />
          {label}
          {required ? <span className="text-rose-500">*</span> : <span className="text-slate-400 normal-case tracking-normal">(optional)</span>}
        </label>
        {value?.dataUrl ? (
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"
            disabled={disabled}
          >
            <XMarkIcon className="h-3.5 w-3.5" />
            Remove
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        disabled={disabled || isProcessing}
        className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-3 file:py-2 file:text-xs file:font-bold file:text-white hover:file:bg-indigo-700 disabled:opacity-60"
      />

      <div className="mt-2 text-[11px] text-slate-500">
        {isProcessing ? 'Compressing image to 100 KB...' : 'JPG, PNG or WebP. The image is compressed before upload.'}
      </div>
      {error ? <div className="mt-2 text-[11px] font-semibold text-rose-600">{error}</div> : null}

      {value?.dataUrl ? (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2">
          <img src={value.dataUrl} alt="Transaction proof" className="h-14 w-14 rounded-lg object-cover" />
          <div className="min-w-0 text-xs text-slate-600">
            <div className="truncate font-semibold text-slate-800">{value.name || 'Compressed image'}</div>
            <div>{Math.ceil((value.size || 0) / 1024)} KB ready to upload</div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TransactionImageField;
